import styled, { css, keyframes } from 'styled-components'
import { Button } from '../../atoms/Button'

export const AIContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 ${({ theme }) => theme.spacing.medium} ${({ theme }) => theme.spacing.medium};
`

export const AIPromptContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`

export const AIExamplesContainer = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.large};
`

export const ExamplesTitle = styled.p`
  margin: ${({ theme }) => theme.spacing.medium} 0;
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  font-weight: ${({ theme }) => theme.typography.fontWeight.normal};
  color: ${({ theme }) => theme.colors.text.tertiary};
  text-transform: none;
`

export const ExampleChips = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.small};
  margin: 0;
`

export const ExampleChip = styled.button`
  padding: ${({ theme }) => theme.spacing.small} ${({ theme }) => theme.spacing.medium};
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  color: ${({ theme }) => theme.colors.text.secondary};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.default};
  text-align: left;
  font-family: inherit;
  line-height: 1.4;

  &:hover {
    background: ${({ theme }) => theme.colors.primary}10;
    border-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.primary};
  }
`

export const AIPromptInputWrapper = styled.div`
  position: relative;
  margin: 0;
`

export const AIPromptTextArea = styled.textarea`
  display: block;
  width: 100%;
  margin: 0;
  padding: ${({ theme }) => theme.spacing.medium};
  padding-bottom: 42px;
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  font-size: 12px;
  font-family: inherit;
  color: ${({ theme }) => theme.colors.text.primary};
  resize: none;
  transition: all ${({ theme }) => theme.transitions.default};
  line-height: 1.5;

  &:focus {
    outline: none;
    background: ${({ theme }) => theme.colors.background.primary};
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primary}1A;
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.text.tertiary};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

export const AIPromptActions = styled.div`
  position: absolute;
  bottom: ${({ theme }) => theme.spacing.small};
  right: ${({ theme }) => theme.spacing.small};
  display: flex;
  gap: ${({ theme }) => theme.spacing.small};
`

const pulseAnimation = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(255, 59, 48, 0.4);
  }
  70% {
    box-shadow: 0 0 0 8px rgba(255, 59, 48, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(255, 59, 48, 0);
  }
`

export const VoiceButton = styled.button<{ $recording?: boolean }>`
  width: 26px;
  height: 26px;
  border: none;
  background: ${({ theme }) => theme.colors.background.primary};
  color: ${({ theme }) => theme.colors.text.secondary};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all ${({ theme }) => theme.transitions.default};
  box-shadow: ${({ theme }) => theme.shadows.sm};

  &:hover {
    background: ${({ theme }) => theme.colors.background.primary};
    color: ${({ theme }) => theme.colors.primary};
    box-shadow: ${({ theme }) => theme.shadows.md};
  }

  ${({ $recording, theme }) =>
    $recording &&
    css`
      background: ${theme.colors.danger};
      color: white;
      animation: ${pulseAnimation} 1.5s infinite;
    `}
`

export const SubmitButton = styled(Button)`
  && {
    width: 26px;
    height: 26px;
    min-width: 26px;
    min-height: 26px;
    padding: 0;
  }
`

// AI Thinking Animation
const thinkingAnimation = keyframes`
  0%, 80%, 100% {
    transform: scale(0);
    opacity: 0.5;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
`

export const AIThinkingContainer = styled.div`
  background: ${({ theme }) => theme.colors.background.tertiary};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  padding: ${({ theme }) => theme.spacing.large};
  text-align: center;
`

export const ThinkingAnimation = styled.div`
  display: flex;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.small};
  margin-bottom: ${({ theme }) => theme.spacing.medium};
`

export const ThinkingDot = styled.div`
  width: 6px;
  height: 6px;
  background: ${({ theme }) => theme.colors.primary};
  border-radius: 50%;
  animation: ${thinkingAnimation} 1.4s infinite ease-in-out both;

  &:nth-child(1) {
    animation-delay: -0.32s;
  }

  &:nth-child(2) {
    animation-delay: -0.16s;
  }
`

export const ThinkingText = styled.p`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0;
`

// AI Response
const slideUpAnimation = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

export const AIResponseContainer = styled.div`
  background: ${({ theme }) => theme.colors.primary}10;
  border: 1px solid ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  padding: ${({ theme }) => theme.spacing.medium};
  animation: ${slideUpAnimation} 0.3s ease-out;
  margin-bottom: ${({ theme }) => theme.spacing.medium};
`

export const ResponseHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
  color: ${({ theme }) => theme.colors.primary};
  font-size: 11px;
  font-weight: 500;
  margin-bottom: ${({ theme }) => theme.spacing.medium};
`

export const ResponseContent = styled.div`
  font-size: 10px;
  color: ${({ theme }) => theme.colors.text.primary};
  line-height: 1.5;

  p {
    margin: 0 0 ${({ theme }) => theme.spacing.small} 0;
  }

  ul {
    margin: ${({ theme }) => theme.spacing.small} 0 0;
    padding-left: 16px;
  }

  li {
    margin-bottom: 3px;
  }
`

// Response History
export const ResponseHistoryContainer = styled.div`
  margin-top: ${({ theme }) => theme.spacing.large};
  padding-top: ${({ theme }) => theme.spacing.large};
  border-top: 1px solid ${({ theme }) => theme.colors.border.light};
  overflow-y: auto;
  max-height: 300px;
`

export const ResponseHistoryHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing.medium};
`

export const ResponseHistoryTitle = styled.h4`
  font-size: 12px;
  font-weight: 600;
  margin: 0;
  color: ${({ theme }) => theme.colors.text.primary};
`

export const ClearHistoryButton = styled.button`
  border: none;
  background: none;
  padding: 2px 6px;
  font-size: 10px;
  color: ${({ theme }) => theme.colors.text.tertiary};
  cursor: pointer;
  border-radius: ${({ theme }) => theme.borderRadius.small};
  transition: all ${({ theme }) => theme.transitions.default};
  font-family: inherit;

  &:hover {
    color: ${({ theme }) => theme.colors.danger};
    background: ${({ theme }) => theme.colors.danger}1A;
  }
`

export const ResponseItem = styled.div`
  padding: ${({ theme }) => theme.spacing.medium};
  background: ${({ theme }) => theme.colors.background.secondary};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  margin-bottom: ${({ theme }) => theme.spacing.medium};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.default};
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.small};

  &:hover {
    background: ${({ theme }) => theme.colors.background.tertiary};
  }
`

export const ResponseItemContent = styled.div`
  flex: 1;
  min-width: 0;
`

export const ResponsePrompt = styled.div`
  font-size: 11px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 3px;
`

export const ResponseExplanation = styled.div`
  font-size: 10px;
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: 1.5;
`

export const SaveFilterButton = styled.button<{ $saved?: boolean }>`
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  border: none;
  background: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${({ theme }) => theme.borderRadius.small};
  transition: all ${({ theme }) => theme.transitions.default};
  color: ${({ theme, $saved }) => ($saved ? theme.colors.primary : theme.colors.text.tertiary)};

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme }) => theme.colors.primary}10;
  }

  ${({ $saved }) =>
    $saved &&
    css`
      cursor: default;
      &:hover {
        background: none;
      }
    `}
`

export const SavingSpinner = styled.div`
  width: 14px;
  height: 14px;
  border: 2px solid ${({ theme }) => theme.colors.border.default};
  border-top-color: ${({ theme }) => theme.colors.primary};
  border-radius: 50%;
  animation: spin 0.6s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`

// Error Message
export const ErrorMessage = styled.div`
  padding: ${({ theme }) => theme.spacing.medium};
  background: ${({ theme }) => theme.colors.danger}1A;
  border: 1px solid ${({ theme }) => theme.colors.danger}4D;
  border-radius: ${({ theme }) => theme.borderRadius.small};
  color: ${({ theme }) => theme.colors.danger};
  font-size: 11px;
  margin-bottom: ${({ theme }) => theme.spacing.medium};
`
